import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');
  const [translations, setTranslations] = useState({});

  // Mapeo de códigos de idioma a nombres de archivos
  const getFileName = (langCode) => {
    const mapping = {
      'es': 'es',
      'en': 'en',
      'eus': 'eus',
      'de': 'de',
      'fr': 'fr',
      'it': 'it',
      'pt': 'pt',
      'ru': 'ru',
      'ca': 'cat',
      'gl': 'gal',
      'no': 'nor',
      'ar': 'ar',
      'zh': 'zh-CN',
      'zh_TW': 'zh-TW',
      'ja': 'ja',
      'ko': 'ko'
    };
    return mapping[langCode] || 'es';
  };

  // Lista de idiomas disponibles
  const availableLanguages = ['es', 'en', 'eus', 'de', 'fr', 'it', 'pt', 'ru', 'ca', 'gl', 'no', 'ar', 'zh', 'zh_TW', 'ja', 'ko'];

  // Cargar traducciones cuando cambia el idioma
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const fileName = getFileName(language);
        const response = await import(`../locales/${fileName}.json`);
        setTranslations(response.default);
        localStorage.setItem('preferredLanguage', language);
        document.documentElement.lang = language;
        
        // Aplicar dirección RTL para árabe
        if (language === 'ar') {
          document.documentElement.dir = 'rtl';
        } else {
          document.documentElement.dir = 'ltr';
        }
      } catch (error) {
        console.error('Error loading translations for', language, ':', error);
        try {
          const fallback = await import(`../locales/es.json`);
          setTranslations(fallback.default);
        } catch (e) {
          console.error('Failed to load fallback translations:', e);
          setTranslations({});
        }
      }
    };

    loadTranslations();
  }, [language]);

  // Inicializar con idioma guardado o del navegador
  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage && availableLanguages.includes(savedLanguage)) {
      setLanguage(savedLanguage);
    } else {
      // Detectar idioma del navegador
      const browserLang = navigator.language.split('-')[0];
      const browserMap = {
        'es': 'es',
        'en': 'en',
        'eu': 'eus',
        'fr': 'fr',
        'de': 'de',
        'it': 'it',
        'pt': 'pt',
        'ru': 'ru',
        'ca': 'ca',
        'gl': 'gl',
        'no': 'no',
        'ar': 'ar',
        'zh': 'zh',
        'ja': 'ja',
        'ko': 'ko'
      };
      const mappedLang = browserMap[browserLang];
      if (mappedLang && availableLanguages.includes(mappedLang)) {
        setLanguage(mappedLang);
      } else {
        setLanguage('es');
      }
    }
  }, []);

  // Función de traducción
  const t = (path, options = {}) => {
    const keys = path.split('.');
    let value = translations;
    
    for (const key of keys) {
      if (value === undefined || value === null) {
        break;
      }
      value = value[key];
    }
    
    if (value === undefined || value === null) {
      if (options.defaultValue) {
        return options.defaultValue;
      }
      // Si no encuentra la traducción, devuelve la última clave
      return keys.pop();
    }
    
    // Si es un objeto y se solicita, devolverlo
    if (typeof value === 'object' && options.returnObjects) {
      return value;
    }
    
    return value;
  };

  // Cambiar idioma
  const changeLanguage = (newLang) => {
    if (availableLanguages.includes(newLang)) {
      setLanguage(newLang);
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      t, 
      changeLanguage, 
      translations,
      currentLanguage: language
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
